import dayjs from 'dayjs';
import {
  TravelTypes,
  HotelRateDisplayPreferences,
  HotelRatePriceSubLabels,
} from '../../../../constants';
import formatHotelCancelationPolicy from '../../../../helpers/formatHotelCancelationPolicy';
import currencySymbol from '../../../../helpers/currencySymbol';
import getHotelRateToDisplay from '../../../../helpers/getHotelRateToDisplay.ts';

const extractPlainTextFromHtml = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  let text = raw;
  // Decode entities first so e.g. &lt;br&gt; becomes <br> and we can strip it
  text = text.replace(/&lt;/gi, '<');
  text = text.replace(/&gt;/gi, '>');
  text = text.replace(/&nbsp;/gi, ' ');
  text = text.replace(/&amp;/gi, '&');
  // Normalise line breaks (all br variants)
  text = text.replace(/<br\s*\/?>/gi, '\n');
  text = text.replace(/<\/(div|p|pre|h[1-6])>/gi, '\n');
  // Strip all remaining tags
  text = text.replace(/<[^>]+>/g, '');
  // Remove any literal "<br>" that might remain (e.g. from odd encoding)
  text = text.replace(/<br\s*\/?>/gi, '\n');
  text = text.replace(/<br>/gi, '\n');
  // Collapse whitespace
  text = text.replace(/\r\n/g, '\n');
  text = text.replace(/\n{3,}/g, '\n\n');
  text = text.replace(/[ \t]+\n/g, '\n');
  // Normalise "/" used as separator (e.g. "Mayfair/ Claridges" -> "Mayfair, Claridge's")
  text = text.replace(/\s*\/\s*/g, ', ');
  return text.trim();
};

const RATE_DISPLAY_FALLBACKS = [
  HotelRateDisplayPreferences.NightlyTotalWithoutTax,
  HotelRateDisplayPreferences.NightlyPrice,
  HotelRateDisplayPreferences.TotalRate,
];

/*
Amount and sub-label are resolved together so the label always describes the amount that is
actually rendered — an unsupported preference (e.g. a stale cookie value) or a rate missing the
requested field falls through to the next candidate instead of silently mislabeling the price.
*/
export const resolveRateToDisplay = (preference, rate) => {
  const candidates = [preference, ...RATE_DISPLAY_FALLBACKS].filter(
    (candidate) => HotelRatePriceSubLabels[candidate] !== undefined,
  );
  const matched = candidates.find((candidate) => getHotelRateToDisplay(candidate, rate) != null);
  if (matched === undefined) return null;

  return {
    amount: getHotelRateToDisplay(matched, rate),
    subLabel: HotelRatePriceSubLabels[matched],
  };
};

export const formatRateAmount = (amount, currencyCode) => {
  const formattedAmount = amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `${currencySymbol(currencyCode)}${formattedAmount}`;
};

const extractBrochureTemplate2Data = (
  brochure,
  roomDescriptions,
  hotelDetailsStore = {},
  options = {},
) => {
  const {
    doShowBookButton = true,
    rateDisplayPreference = HotelRateDisplayPreferences.NightlyTotalWithoutTax,
  } = options;
  if (!brochure) return { type: 'doc', content: [] };

  const createTextParagraph = (text, cssClass, align = 'left') => ({
    type: 'paragraph',
    attrs: { textAlign: align, class: cssClass },
    content: text ? [{ type: 'text', text }] : [{ type: 'text', text: '\u200B' }],
  });

  // Paragraph with line breaks (for list-like content e.g. "Attraction 1.0 MI" per line)
  const createParagraphWithLineBreaks = (lines, cssClass) => {
    const trimmed = lines.filter(Boolean).slice(0, 30);
    if (!trimmed.length) return createTextParagraph('', cssClass);
    const content = [];
    trimmed.forEach((line, i) => {
      if (i > 0) content.push({ type: 'hardBreak' });
      content.push({ type: 'text', text: line.trim() });
    });
    return { type: 'paragraph', attrs: { class: cssClass }, content };
  };

  const createHeading = (level, text, cssClass) => ({
    type: 'heading',
    attrs: {
      textAlign: 'left',
      level,
      class: cssClass,
    },
    content: [{ type: 'text', text }],
  });

  const createImage = (src, alt, attrs = {}) => ({
    type: 'image',
    attrs: {
      src,
      alt,
      ...attrs,
    },
  });

  // Legacy helper kept for potential future use; currently unused.
  // const createBulletList = (items, cssClass) => ({
  //   type: 'bulletList',
  //   attrs: { class: cssClass },
  //   content: items.filter(Boolean).map((text) => ({
  //     type: 'listItem',
  //     content: [createTextParagraph(text)],
  //   })),
  // });

  const createTwoColumnTableList = (items = []) => {
    const rows = [];

    for (let i = 0; i < items.length; i += 2) {
      const first = items[i];
      const second = items[i + 1];
      const cells = [];
      cells.push({
        type: 'tableCell',
        attrs: {
          class: 'tt2-info-cell',
          'data-noBorder': 'true',
          style: `
            border: none !important;
            padding: 0 16px 0 0 !important;
            margin: 0 !important;
            text-align: left;
            vertical-align: top;
            width: 50%;
          `,
        },
        content: first ? [createTextParagraph(first)] : [],
      });

      cells.push({
        type: 'tableCell',
        attrs: {
          class: 'tt2-info-cell',
          'data-noBorder': 'true',
          style: `
            border: none !important;
            padding: 0 !important;
            margin: 0 !important;
            text-align: left;
            vertical-align: top;
            width: 50%;
          `,
        },
        content: second ? [createTextParagraph(second)] : [],
      });
      rows.push({
        type: 'tableRow',
        content: cells,
      });
    }

    return {
      type: 'table',
      attrs: {
        class: 'tt2-info-table',
        'data-noBorder': 'true',
        style: `
          border: none !important;
          border-collapse: collapse !important;
          width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
        `,
      },
      content: rows,
    };
  };

  const createPhotoGrid = (images = []) => {
    const validImages = images.filter(Boolean);
    if (!validImages.length) return { nodes: null };

    const imageNodes = validImages.map((src) =>
      createImage(src, 'Photo', {
        class: 'tt2-photo',
      }),
    );

    if (!imageNodes.length) {
      return { nodes: null };
    }

    const paragraphNode = {
      type: 'paragraph',
      attrs: {
        class: 'tt2-photo-grid',
      },
      content: imageNodes,
    };

    return { nodes: [paragraphNode] };
  };

  const trips = Array.isArray(brochure.trips)
    ? brochure.trips
    : Object.values(brochure.trips || {});

  // Collect all hotel segments across all trips
  const hotelSegments = [];
  trips.forEach((trip) => {
    (trip.tripSegments || []).forEach((segment) => {
      if (segment.itinerary?.mode === TravelTypes.hotel) {
        hotelSegments.push({
          trip,
          segment,
          itinerary: segment.itinerary,
          hotel: segment.itinerary.hotel,
        });
      }
    });
  });

  const doc = {
    type: 'doc',
    content: [],
  };

  if (!hotelSegments.length) {
    return doc;
  }

  // Placeholder matching HotelItineraryContentV2 "No photo available"
  // (bg #f5f6f8, shapes #e2e5ea, text #b0b5be)
  const placeholderImageBase64 = `data:image/svg+xml;base64,${btoa(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 320" preserveAspectRatio="xMidYMid meet">' +
      '<rect width="320" height="320" fill="#f5f6f8"/>' +
      '<g transform="translate(100, 116)">' +
      '<rect width="120" height="68" rx="8" fill="#e2e5ea"/>' +
      '<rect x="15" y="82" width="90" height="8" rx="5" fill="#e2e5ea"/>' +
      '<rect x="30" y="104" width="60" height="8" rx="5" fill="#e2e5ea"/>' +
      '<text x="60" y="132" text-anchor="middle" fill="#b0b5be" font-size="10" font-weight="600" font-family="Arial,sans-serif" letter-spacing="0.1em" style="text-transform:uppercase">No photo available</text>' +
      '</g></svg>',
  )}`;

  // Render a full block (hotel header + rooms) for each hotel segment
  hotelSegments.forEach(({ segment, itinerary, hotel }, hotelIndex) => {
    const start = segment?.startDateTime || hotel.checkInStartTime;
    const end = segment?.endDateTime || hotel.checkOutEndTime;
    const startStr = start ? dayjs(start).format('MMM D') : '';
    const endStr = end ? dayjs(end).format('MMM D') : '';

    // Add a small divider between hotels (but not before the first)
    if (hotelIndex > 0) {
      doc.content.push({
        type: 'horizontalRule',
      });
    }

    const hotelName = itinerary.name || hotel.name || '';
    const address = itinerary.fullAddress || hotel.fullAddress || itinerary.address || '';
    const dateLine = startStr && endStr ? `${startStr} – ${endStr}` : '';

    if (hotelName) {
      // Match "Proposal for" heading semantics (h2-level)
      doc.content.push(createHeading(2, hotelName, 'tt2-hotel-name'));
    }
    if (address) {
      doc.content.push(createTextParagraph(address, 'tt2-hotel-address'));
    }
    if (dateLine) {
      doc.content.push(createTextParagraph(dateLine, 'tt2-hotel-dates'));
    }

    // Hero per hotel (fallback to "No photo" placeholder when missing)
    const heroImage = hotel.image || hotel.images?.[0]?.url || placeholderImageBase64;
    if (heroImage) {
      doc.content.push(
        createImage(heroImage, itinerary.name || hotel.name || 'Hotel image', {
          class: 'tt2-hero-image',
          style: `
            width: 100%;
            height: 320px;
            max-height: 320px;
            object-fit: cover;
            display: block;
          `,
        }),
      );
    }

    const products = hotel.products || [];
    const roomsContent = [];

    // Rooms for this hotel
    products.forEach((product, index) => {
      const room = product.rooms?.[0] || product;
      const improved = roomDescriptions?.[product.code]?.description;

      let primaryName;
      let secondaryName;

      if (improved?.roomTitle) {
        // ChatGPT-improved room title already in "1 King Bed - Hyatt Prive" style
        primaryName = improved.roomTitle;
        secondaryName = '';
      } else {
        const bedTypeName = room?.bedTypes?.length ? room.bedTypes[0] : '';
        primaryName = room?.type || bedTypeName || room?.category || '';
        // Prefer rate/program name (e.g. "Hyatt Prive"), do NOT default to chain name here.
        const rateLabel =
          product?.rateName ||
          product?.rateOptionName ||
          product?.programName ||
          product?.rateProgramName ||
          product?.name ||
          '';
        secondaryName = rateLabel;
      }
      const headingParts = [primaryName, secondaryName].filter(Boolean);
      const heading = headingParts.length
        ? headingParts.join(' - ')
        : primaryName || secondaryName || `Room ${index + 1}`;

      // Only show a photo if we have a room-specific image; don't fall back to hotel hero.
      const roomImage = room?.image || product.image || null;
      const desc = room?.formattedDesc || room?.desc || room?.originalDesc || '';
      const size = room?.roomSize || '';
      const beds = room?.bedTypes?.length ? room.bedTypes.join(', ') : '';

      const priceObj = product.roomPrice || {};
      const rateForDisplay = {
        averageNightlyRate: priceObj.averageNightlyRate,
        averageNightlyRateBeforeTax: priceObj.averageNightlyRateBeforeTax,
        price: priceObj.originalPrice ?? priceObj.price,
      };
      const rateToDisplay = resolveRateToDisplay(rateDisplayPreference, rateForDisplay);
      const currencyCode = priceObj.currencyCode || itinerary?.cost?.currencyCode || 'USD';
      const nightlyText = rateToDisplay ? formatRateAmount(rateToDisplay.amount, currencyCode) : '';
      const priceSubLabel = rateToDisplay ? rateToDisplay.subLabel : '';

      const refundableLabel = product.nonRefundable ? 'Non-refundable' : 'Refundable';
      const cancellationPolicyRaw = product.cancellationPolicy || '';
      const cancellationPolicyText = cancellationPolicyRaw
        ? formatHotelCancelationPolicy(cancellationPolicyRaw)
        : '';

      const highlights = [];
      if (!improved?.sections?.length) {
        // Fallback when there is no AI‑improved structure
        if (size) highlights.push(size);
        if (beds) highlights.push(beds);
        if (desc) highlights.push(desc);
      }

      // Three-column layout when photo exists: photo | title+desc | price
      // Otherwise two-column: title+desc (full width) | price
      const cells = [];

      // 1) Thumbnail (optional)
      if (roomImage) {
        cells.push({
          type: 'tableCell',
          attrs: {
            class: 'tt2-room-thumb-cell',
            'data-noBorder': 'true',
            style: `
              width: 140px;
              padding: 0 16px 0 0;
              border: none;
              vertical-align: middle;
            `,
          },
          content: [
            createImage(roomImage, heading, {
              class: 'tt2-room-thumb',
              style: `
              width: 100%;
              height: 110px;
              max-height: 110px;
              object-fit: cover;
              display: block;
            `,
            }),
          ],
        });
      }

      // 2) Title + description
      const rightContent = [];
      rightContent.push(createHeading(3, heading, 'tt2-room-title'));
      if (desc) {
        rightContent.push(createTextParagraph(desc, 'tt2-room-subtitle'));
      }
      cells.push({
        type: 'tableCell',
        attrs: {
          class: roomImage ? 'tt2-room-main-cell' : 'tt2-room-main-full-cell',
          'data-noBorder': 'true',
          style: `
            border: none;
            vertical-align: middle;
            padding: 0;
          `,
        },
        content: rightContent,
      });

      // 3) Price column + inline \"Book\" CTA
      const priceParagraphs = [];
      if (nightlyText) {
        priceParagraphs.push(createTextParagraph(nightlyText, 'tt2-price-main', 'right'));
        priceParagraphs.push(createTextParagraph(priceSubLabel, 'tt2-price-sub', 'right'));
      }
      // Simple visual book button placeholder
      // (functional TripBookButton lives in original brochure)
      if (doShowBookButton) {
        priceParagraphs.push(createTextParagraph('Book', 'tt2-room-book-button', 'right'));
      }

      cells.push({
        type: 'tableCell',
        attrs: {
          class: 'tt2-room-price-cell',
          'data-noBorder': 'true',
          style: `
            border: none;
            vertical-align: middle;
            padding: 0 0 0 12px;
            width: 170px;
            text-align: right;
          `,
        },
        content: priceParagraphs,
      });

      const roomContent = [];

      roomContent.push({
        type: 'table',
        attrs: {
          class: 'tt2-room-row',
          'data-noBorder': 'true',
          style: `
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
            margin: 0;
            border: 1px solid #d1d5db;
            border-radius: 12px;
            background-color: #ffffff;
            box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
          `,
        },
        content: [
          {
            type: 'tableRow',
            content: cells,
          },
        ],
      });

      // Improved description: show each section with its own title + multi‑column infos
      if (improved?.sections?.length) {
        improved.sections.forEach((section) => {
          const infos = (section.infos || []).filter((info) => !!info && typeof info === 'string');
          if (!section.sectionTitle && !infos.length) return;

          if (section.sectionTitle) {
            roomContent.push(createTextParagraph(section.sectionTitle.trim(), 'tt2-room-label'));
          }
          if (infos.length) {
            roomContent.push(createTwoColumnTableList(infos));
          }
        });
      } else if (highlights.length > 0) {
        // Legacy fallback: simple "Highlights" block
        roomContent.push(createTextParagraph('Highlights', 'tt2-room-label'));
        roomContent.push(createTwoColumnTableList(highlights));
      }

      // Refundability & cancellation section under the rate description
      if (refundableLabel || cancellationPolicyText) {
        roomContent.push(createTextParagraph('Refundability & cancellation', 'tt2-room-label'));
        const refundLines = [];
        if (refundableLabel) refundLines.push(refundableLabel);
        if (cancellationPolicyText) refundLines.push(cancellationPolicyText);
        roomContent.push(createParagraphWithLineBreaks(refundLines, 'tt2-room-refund'));
      }

      // Wrap this room in a segment container so we can style a card border reliably
      roomsContent.push({
        type: 'segmentContainer',
        attrs: {
          segmentType: 'hotel-room',
          segmentId: product.code,
        },
        content: roomContent,
      });
    });

    // About: hotelDetails.info, brochure hotel.info, then fallbacks
    const detailsHotel = hotelDetailsStore[itinerary.hotelKey]?.hotel;
    const rawAboutSource =
      (typeof detailsHotel?.info === 'string' && detailsHotel.info) ||
      (typeof hotel.info === 'string' && hotel.info) ||
      (typeof itinerary.info === 'string' && itinerary.info) ||
      itinerary.description ||
      hotel.description ||
      hotel.fullDescription ||
      hotel.longDescription ||
      (hotel.amenities?.length ? `Amenities: ${hotel.amenities.slice(0, 12).join(', ')}.` : null) ||
      'No description available.';

    const plainAbout = extractPlainTextFromHtml(rawAboutSource);
    const aboutContent = [];
    const rawBlocks = plainAbout
      ? plainAbout
          .split(/\n{2,}/)
          .map((b) => b.trim())
          .filter(Boolean)
      : [];
    let blocks;
    if (rawBlocks.length) {
      blocks = rawBlocks;
    } else if (plainAbout) {
      blocks = [plainAbout];
    } else {
      blocks = [];
    }

    blocks.forEach((block) => {
      const lines = block
        .split(/\n/)
        .map((l) => l.trim())
        .filter(Boolean);
      const avgLineLength = lines.length
        ? lines.reduce((s, l) => s + l.length, 0) / lines.length
        : 0;
      const miSuffix = /\d+\.?\d*\s*MI\s*$/i;
      const looksLikeDistanceList = lines.length >= 2 && lines.some((l) => miSuffix.test(l));
      const listLike = lines.length >= 3 && (avgLineLength < 55 || looksLikeDistanceList);

      if (listLike) {
        aboutContent.push(createParagraphWithLineBreaks(lines, 'tt2-about'));
      } else {
        const flowed = block
          .replace(/\n/g, ' ')
          .replace(/\s{2,}/g, ' ')
          .trim();
        if (flowed) aboutContent.push(createTextParagraph(flowed, 'tt2-about'));
      }
    });

    // Photos section – show all reasonably distinct photos for this hotel
    const photoSources = [];
    const seen = new Set();
    const addPhoto = (src) => {
      if (!src || src === placeholderImageBase64) return;
      if (seen.has(src)) return;
      seen.add(src);
      photoSources.push(src);
    };

    // Use hero, full hotel gallery, and room/product images for the Photos tab
    addPhoto(hotel.image);

    (hotel.images || []).forEach((img) => {
      if (!img) return;
      const src = img.url || img.src || img.image || null;
      addPhoto(src);
    });

    (products || []).forEach((product) => {
      const room = product.rooms?.[0] || product;
      addPhoto(room?.image);
      addPhoto(product.image);
    });

    const { nodes: photoGridNodes } = createPhotoGrid(photoSources);
    const photosContent = [];
    if (photoGridNodes && photoGridNodes.length) {
      photoGridNodes.forEach((node) => photosContent.push(node));
    }

    doc.content.push({
      type: 'hotelTabsContainer',
      content: [
        { type: 'hotelTabSection', attrs: { tab: 'rooms' }, content: roomsContent },
        { type: 'hotelTabSection', attrs: { tab: 'about' }, content: aboutContent },
        { type: 'hotelTabSection', attrs: { tab: 'photos' }, content: photosContent },
      ],
    });
  });

  return doc;
};

export default extractBrochureTemplate2Data;
